/**
 * Le Desk — Global auto-capitalisation helper.
 *
 * Applied at the primitive layer (shadcn <Input> + <Textarea>) so EVERY
 * form across the whole app inherits the same behaviour without any
 * per-field wiring:
 *
 *   • The FIRST character typed in a free-text field is forced to
 *     uppercase (locale-aware so “é / à / ô…” become “É / À / Ô…”).
 *   • Anything the user types after that is left untouched — no
 *     surprises for acronyms (“iOS”, “URL”), URLs, tokens, etc.
 *   • Applies to <textarea> unconditionally, and to <input type="text">
 *     (or <input> with no type set — the shadcn default). Password /
 *     email / number / URL / date / search inputs are skipped so we
 *     never break search bars, credentials, dates, etc.
 *   • Opt-out per field via the `data-no-capitalize` attribute for the
 *     rare case a field needs the raw first char untouched.
 *
 * Returns an event handler you can drop straight into onChange.
 */
export const TEXTISH_INPUT_TYPES = new Set(["text", "search"]);
// NOTE: "search" is included because our search bars are already
// case-insensitive on the server and users type queries — but we
// explicitly opt-out via data-no-capitalize on those to preserve UX.

export function shouldAutoCapitalizeInput(type, props) {
  if (props && props["data-no-capitalize"] !== undefined) return false;
  if (type === undefined || type === null || type === "") return true; // shadcn default
  return type === "text";
}

/**
 * Wraps a caller-supplied onChange so the FIRST char of the field is
 * capitalised on the fly. Safe with React controlled inputs because we
 * mutate the SyntheticEvent's `target.value` before delegating.
 */
export function withFirstLetterCapitalized(onChange) {
  return function handleChange(e) {
    const v = e?.target?.value;
    if (typeof v === "string" && v.length > 0) {
      const first = v.charAt(0);
      const cap = first.toLocaleUpperCase("fr-FR");
      if (first !== cap) {
        // eslint-disable-next-line no-param-reassign
        e.target.value = cap + v.slice(1);
      }
    }
    if (typeof onChange === "function") onChange(e);
  };
}
